
// Enhanced ProfileEditModal.js for React/Web Admin Dashboard
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Eye, EyeOff, Save, RotateCcw, Trophy, Settings } from 'lucide-react';
import localforage from 'localforage';

const ProfileEditModal = ({ 
  isVisible, 
  onClose, 
  userInfo, 
  onProfileUpdate,
  isAdmin = false 
}) => {
  // Existing profile states
  const [profileData, setProfileData] = useState({
    username: '',
    email: '',
    profileImage: null,
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  // NEW: Admin leaderboard states
  const [adminStats, setAdminStats] = useState({
    totalCheckouts: 0,
    todayCheckouts: 0,
    isParticipating: true,
    currentRank: null,
    position: null
  });
  
  const [showAdminSection, setShowAdminSection] = useState(false);
  const [adminStatsLoading, setAdminStatsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('profile'); // 'profile' or 'leaderboard'
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  const BASE_URL = 'https://lager-app-api.sunrise-avengers.ch:443';
  
  // Admin Rank System (same as other components)
  const ADMIN_RANKS = [
    { 
      id: 1, 
      name: 'Novice', 
      minCheckouts: 0, 
      maxCheckouts: 9, 
      color: '#64748B', 
      icon: '👤',
      description: 'Starting out'
    },
    { 
      id: 2, 
      name: 'Helper', 
      minCheckouts: 10, 
      maxCheckouts: 24, 
      color: '#059669', 
      icon: '🤝',
      description: 'Lending a hand'
    },
    { 
      id: 3, 
      name: 'Guardian', 
      minCheckouts: 25, 
      maxCheckouts: 49, 
      color: '#0D9488', 
      icon: '🛡️',
      description: 'Watching over'
    },
    { 
      id: 4, 
      name: 'Coordinator', 
      minCheckouts: 50, 
      maxCheckouts: 99, 
      color: '#0284C7', 
      icon: '📋',
      description: 'Organizing efficiently'
    },
    { 
      id: 5, 
      name: 'Supervisor', 
      minCheckouts: 100, 
      maxCheckouts: 199, 
      color: '#7C3AED', 
      icon: '👁️',
      description: 'Overseeing operations'
    },
    { 
      id: 6, 
      name: 'Master', 
      minCheckouts: 200, 
      maxCheckouts: 399, 
      color: '#DC2626', 
      icon: '🎯',
      description: 'Expert level'
    },
    { 
      id: 7, 
      name: 'Legend', 
      minCheckouts: 400, 
      maxCheckouts: 999, 
      color: '#B45309', 
      icon: '👑',
      description: 'Legendary status'
    }
  ];
  
  // Get current admin rank based on checkouts
  const getCurrentRank = (checkouts) => {
    return ADMIN_RANKS.find(rank => 
      checkouts >= rank.minCheckouts && checkouts <= rank.maxCheckouts
    ) || ADMIN_RANKS[0];
  };
  
  // Load user data when modal opens
  useEffect(() => {
    if (isVisible && userInfo) {
      setProfileData({
        username: userInfo.username || '',
        email: userInfo.email || '',
        profileImage: userInfo.profileImage || null,
        oldPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      
      // Load admin stats if user is admin
      if (isAdmin) {
        fetchAdminStats();
        setShowAdminSection(true);
      }
      
      setError('');
      setSuccessMessage('');
    }
  }, [isVisible, userInfo, isAdmin]);
  
  // Fetch admin leaderboard stats
  const fetchAdminStats = async () => {
    try {
      setAdminStatsLoading(true);
      const token = await localforage.getItem('token');
      
      const response = await fetch(`${BASE_URL}/api/admin/leaderboard/personal`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        const stats = data.personalStats;
        
        setAdminStats({
          totalCheckouts: stats.totalCheckouts || 0,
          todayCheckouts: stats.todayCheckouts || 0,
          isParticipating: stats.isParticipating !== false,
          currentRank: getCurrentRank(stats.totalCheckouts || 0),
          position: null // This would need to be calculated from leaderboard
        });
      }
    } catch (error) {
      console.error('Error fetching admin stats:', error);
      setError('Failed to load admin statistics');
    } finally {
      setAdminStatsLoading(false);
    }
  };
  
  // Save profile changes
  const handleSaveProfile = async () => {
    try {
      setIsSaving(true);
      setError('');
      
      const token = await localforage.getItem('token');
      const formData = new FormData();
      
      // Basic profile data
      formData.append('username', profileData.username);
      formData.append('email', profileData.email);
      
      // Profile image
      if (profileData.profileImage && typeof profileData.profileImage !== 'string') {
        formData.append('profileImage', profileData.profileImage);
      }
      
      // Password change
      if (profileData.newPassword) {
        if (profileData.newPassword !== profileData.confirmPassword) {
          setError('New passwords do not match');
          return;
        }
        
        if (!profileData.oldPassword) {
          setError('Please enter your current password');
          return;
        }
        
        formData.append('oldPassword', profileData.oldPassword);
        formData.append('newPassword', profileData.newPassword);
      }
      
      const response = await fetch(`${BASE_URL}/api/auth/update-profile`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (response.ok) {
        setSuccessMessage('Profile updated successfully!');
        
        // Reset password fields
        setProfileData(prev => ({
          ...prev,
          oldPassword: '',
          newPassword: '',
          confirmPassword: ''
        }));
        
        if (onProfileUpdate) {
          onProfileUpdate();
        }
        
        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to update profile');
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      setError('An error occurred while updating your profile');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Save admin stats
  const handleSaveAdminStats = async () => {
    try {
      setIsSaving(true);
      setError('');
      
      const token = await localforage.getItem('token');
      
      const response = await fetch(`${BASE_URL}/api/admin/leaderboard/personal`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          totalCheckouts: adminStats.totalCheckouts,
          isParticipating: adminStats.isParticipating
        })
      });
      
      if (response.ok) {
        setSuccessMessage('Admin statistics updated successfully!');
        await fetchAdminStats(); // Refresh stats
        
        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to update admin statistics');
      }
    } catch (error) {
      console.error('Error updating admin stats:', error);
      setError('An error occurred while updating admin statistics');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Reset admin stats
  const handleResetAdminStats = async () => {
    if (!window.confirm('Are you sure you want to reset your admin statistics? This cannot be undone.')) {
      return;
    }
    
    try {
      setIsSaving(true);
      const token = await localforage.getItem('token');
      
      const response = await fetch(`${BASE_URL}/api/admin/leaderboard/reset`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          adminId: userInfo.id,
          campId: null // Reset for all camps, or specify camp
        })
      });
      
      if (response.ok) {
        setSuccessMessage('Admin statistics reset successfully!');
        await fetchAdminStats(); // Refresh stats
        
        setTimeout(() => {
          setSuccessMessage('');
        }, 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to reset admin statistics');
      }
    } catch (error) {
      console.error('Error resetting admin stats:', error);
      setError('An error occurred while resetting admin statistics');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Handle file selection
  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setProfileData(prev => ({ ...prev, profileImage: file }));
    }
  };
  
  if (!isVisible) return null;
  
  return (
    <div style={styles.modalOverlay}>
      <motion.div 
        style={styles.modal}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>Edit Profile</h2>
          <button style={styles.closeButton} onClick={onClose}>
            <X size={24} />
          </button>
        </div>
        
        {/* Tabs (only show if admin) */}
        {isAdmin && (
          <div style={styles.tabContainer}>
            <button
              style={[
                styles.tab,
                activeTab === 'profile' && styles.tabActive
              ]}
              onClick={() => setActiveTab('profile')}
            >
              <Settings size={16} />
              Profile Settings
            </button>
            <button
              style={[
                styles.tab,
                activeTab === 'leaderboard' && styles.tabActive
              ]}
              onClick={() => setActiveTab('leaderboard')}
            >
              <Trophy size={16} />
              Leaderboard Stats
            </button>
          </div>
        )}
        
        {/* Content */}
        <div style={styles.modalContent}>
          <AnimatePresence mode="wait">
            {activeTab === 'profile' ? (
              <motion.div
                key="profile"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
              >
                {/* Profile Image Section */}
                <div style={styles.imageSection}>
                  <div style={styles.imageContainer}>
                    <img
                      src={typeof profileData.profileImage === 'string' 
                        ? profileData.profileImage 
                        : (profileData.profileImage 
                          ? URL.createObjectURL(profileData.profileImage) 
                          : '/api/placeholder/100/100')}
                      alt="Profile"
                      style={styles.profileImage}
                    />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageChange}
                      style={styles.fileInput}
                      id="profile-image-input"
                    />
                    <label htmlFor="profile-image-input" style={styles.imageUploadButton}>
                      Change Photo
                    </label>
                  </div>
                </div>
                
                {/* Form Fields */}
                <div style={styles.formGroup}>
                  <label style={styles.label}>Username</label>
                  <input
                    type="text"
                    value={profileData.username}
                    onChange={(e) => setProfileData(prev => ({ ...prev, username: e.target.value }))}
                    style={styles.input}
                  />
                </div>
                
                <div style={styles.formGroup}>
                  <label style={styles.label}>Email</label>
                  <input
                    type="email"
                    value={profileData.email}
                    onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                    style={styles.input}
                  />
                </div>
                
                {/* Password Change Section */}
                <div style={styles.passwordSection}>
                  <h3 style={styles.sectionTitle}>Change Password</h3>
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Current Password</label>
                    <div style={styles.passwordInputContainer}>
                      <input
                        type={showPassword ? "text" : "password"}
                        value={profileData.oldPassword}
                        onChange={(e) => setProfileData(prev => ({ ...prev, oldPassword: e.target.value }))}
                        style={styles.passwordInput}
                        placeholder="Enter current password"
                      />
                      <button
                        type="button"
                        style={styles.passwordToggle}
                        onClick={() => setShowPassword(!showPassword)}
                      >
                        {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                      </button>
                    </div>
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>New Password</label>
                    <input
                      type={showPassword ? "text" : "password"}
                      value={profileData.newPassword}
                      onChange={(e) => setProfileData(prev => ({ ...prev, newPassword: e.target.value }))}
                      style={styles.input}
                      placeholder="Enter new password"
                    />
                  </div>
                  
                  <div style={styles.formGroup}>
                    <label style={styles.label}>Confirm New Password</label>
                    <input
                      type={showPassword ? "text" : "password"}
                      value={profileData.confirmPassword}
                      onChange={(e) => setProfileData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                      style={styles.input}
                      placeholder="Confirm new password"
                    />
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="leaderboard"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
              >
                {adminStatsLoading ? (
                  <div style={styles.loadingContainer}>
                    <div style={styles.spinner}></div>
                    <p>Loading admin statistics...</p>
                  </div>
                ) : (
                  <div>
                    {/* Current Rank Display */}
                    <div style={[styles.rankCard, { backgroundColor: adminStats.currentRank?.color || '#64748B' }]}>
                      <div style={styles.rankIcon}>
                        {adminStats.currentRank?.icon || '👤'}
                      </div>
                      <div style={styles.rankInfo}>
                        <h3 style={styles.rankName}>{adminStats.currentRank?.name || 'Novice'}</h3>
                        <p style={styles.rankDescription}>{adminStats.currentRank?.description || 'Starting out'}</p>
                      </div>
                      <div style={styles.rankStats}>
                        <div style={styles.statItem}>
                          <span style={styles.statNumber}>{adminStats.totalCheckouts}</span>
                          <span style={styles.statLabel}>Total</span>
                        </div>
                        <div style={styles.statItem}>
                          <span style={styles.statNumber}>{adminStats.todayCheckouts}</span>
                          <span style={styles.statLabel}>Today</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Stats Edit Form */}
                    <div style={styles.statsEditSection}>
                      <h3 style={styles.sectionTitle}>Edit Statistics</h3>
                      
                      <div style={styles.formGroup}>
                        <label style={styles.label}>Total Checkouts</label>
                        <input
                          type="number"
                          min="0"
                          value={adminStats.totalCheckouts}
                          onChange={(e) => setAdminStats(prev => ({ 
                            ...prev, 
                            totalCheckouts: parseInt(e.target.value) || 0,
                            currentRank: getCurrentRank(parseInt(e.target.value) || 0)
                          }))}
                          style={styles.input}
                        />
                        <div style={styles.rankPreview}>
                          New Rank: <span style={{
                            color: getCurrentRank(adminStats.totalCheckouts).color,
                            fontWeight: '600'
                          }}>
                            {getCurrentRank(adminStats.totalCheckouts).icon} {getCurrentRank(adminStats.totalCheckouts).name}
                          </span>
                        </div>
                      </div>
                      
                      <div style={styles.formGroup}>
                        <label style={styles.checkboxLabel}>
                          <input
                            type="checkbox"
                            checked={adminStats.isParticipating}
                            onChange={(e) => setAdminStats(prev => ({ ...prev, isParticipating: e.target.checked }))}
                            style={styles.checkbox}
                          />
                          <span style={styles.checkboxText}>Participate in leaderboard</span>
                        </label>
                        <p style={styles.helpText}>
                          Uncheck this if you don't want to appear on the public leaderboard
                        </p>
                      </div>
                    </div>
                    
                    {/* Admin Actions */}
                    <div style={styles.adminActions}>
                      <button
                        style={styles.resetButton}
                        onClick={handleResetAdminStats}
                        disabled={isSaving}
                      >
                        <RotateCcw size={16} />
                        Reset All Stats
                      </button>
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
          
          {/* Error/Success Messages */}
          {error && (
            <motion.div 
              style={styles.errorMessage}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {error}
            </motion.div>
          )}
          
          {successMessage && (
            <motion.div 
              style={styles.successMessage}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {successMessage}
            </motion.div>
          )}
        </div>
        
        {/* Footer */}
        <div style={styles.modalFooter}>
          <button style={styles.cancelButton} onClick={onClose}>
            Cancel
          </button>
          <button 
            style={styles.saveButton} 
            onClick={activeTab === 'profile' ? handleSaveProfile : handleSaveAdminStats}
            disabled={isSaving}
          >
            {isSaving ? (
              <div style={styles.buttonSpinner}></div>
            ) : (
              <Save size={16} />
            )}
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

// Enhanced styles for the modal
const styles = {
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    backdropFilter: 'blur(4px)',
  },
  
  modal: {
    backgroundColor: '#FFFFFF',
    borderRadius: '16px',
    width: '90%',
    maxWidth: '600px',
    maxHeight: '90vh',
    overflow: 'hidden',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
    display: 'flex',
    flexDirection: 'column',
  },
  
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '24px',
    borderBottom: '1px solid #E5E7EB',
    backgroundColor: '#F8FAFC',
  },
  
  modalTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#1F2937',
    margin: 0,
  },
  
  closeButton: {
    width: '40px',
    height: '40px',
    borderRadius: '20px',
    border: 'none',
    backgroundColor: '#E5E7EB',
    color: '#374151',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  
  tabContainer: {
    display: 'flex',
    borderBottom: '1px solid #E5E7EB',
    backgroundColor: '#F8FAFC',
  },
  
  tab: {
    flex: 1,
    padding: '16px 24px',
    border: 'none',
    backgroundColor: 'transparent',
    color: '#6B7280',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    borderBottom: '2px solid transparent',
    transition: 'all 0.2s ease',
  },
  
  tabActive: {
    color: '#3B82F6',
    borderBottomColor: '#3B82F6',
    backgroundColor: '#FFFFFF',
  },
  
  modalContent: {
    flex: 1,
    padding: '24px',
    overflowY: 'auto',
  },
  
  // Profile Tab Styles
  imageSection: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '32px',
  },
  
  imageContainer: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
  },
  
  profileImage: {
    width: '120px',
    height: '120px',
    borderRadius: '60px',
    objectFit: 'cover',
    border: '4px solid #E5E7EB',
  },
  
  fileInput: {
    display: 'none',
  },
  
  imageUploadButton: {
    padding: '8px 16px',
    backgroundColor: '#3B82F6',
    color: '#FFFFFF',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  
  formGroup: {
    marginBottom: '24px',
  },
  
  label: {
    display: 'block',
    fontSize: '14px',
    fontWeight: '600',
    color: '#374151',
    marginBottom: '8px',
  },
  
  input: {
    width: '100%',
    padding: '12px',
    borderRadius: '8px',
    border: '1px solid #D1D5DB',
    fontSize: '16px',
    transition: 'all 0.2s ease',
    boxSizing: 'border-box',
  },
  
  passwordSection: {
    marginTop: '32px',
    paddingTop: '24px',
    borderTop: '1px solid #E5E7EB',
  },
  
  sectionTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#1F2937',
    marginBottom: '16px',
  },
  
  passwordInputContainer: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
  },
  
  passwordInput: {
    width: '100%',
    padding: '12px 48px 12px 12px',
    borderRadius: '8px',
    border: '1px solid #D1D5DB',
    fontSize: '16px',
    transition: 'all 0.2s ease',
    boxSizing: 'border-box',
  },
  
  passwordToggle: {
    position: 'absolute',
    right: '12px',
    background: 'none',
    border: 'none',
    color: '#6B7280',
    cursor: 'pointer',
    padding: '4px',
  },
  
  // Leaderboard Tab Styles
  rankCard: {
    borderRadius: '16px',
    padding: '24px',
    color: '#FFFFFF',
    marginBottom: '32px',
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
  
  rankIcon: {
    fontSize: '48px',
    minWidth: '60px',
    textAlign: 'center',
  },
  
  rankInfo: {
    flex: 1,
  },
  
  rankName: {
    fontSize: '24px',
    fontWeight: '700',
    margin: '0 0 8px 0',
  },
  
  rankDescription: {
    fontSize: '16px',
    opacity: 0.9,
    margin: 0,
  },
  
  rankStats: {
    display: 'flex',
    gap: '24px',
  },
  
  statItem: {
    textAlign: 'center',
  },
  
  statNumber: {
    display: 'block',
    fontSize: '24px',
    fontWeight: '700',
  },
  
  statLabel: {
    display: 'block',
    fontSize: '12px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    opacity: 0.9,
    marginTop: '4px',
  },
  
  statsEditSection: {
    marginBottom: '24px',
  },
  
  rankPreview: {
    marginTop: '8px',
    fontSize: '14px',
    color: '#6B7280',
  },
  
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
    color: '#374151',
  },
  
  checkbox: {
    marginRight: '12px',
    transform: 'scale(1.2)',
  },
  
  checkboxText: {
    marginLeft: '8px',
  },
  
  helpText: {
    fontSize: '14px',
    color: '#6B7280',
    marginTop: '8px',
  },
  
  adminActions: {
    display: 'flex',
    justifyContent: 'flex-start',
    paddingTop: '16px',
    borderTop: '1px solid #E5E7EB',
  },
  
  resetButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    borderRadius: '6px',
    border: '1px solid #EF4444',
    backgroundColor: 'transparent',
    color: '#EF4444',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  
  // Loading and Message Styles
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    color: '#6B7280',
  },
  
  spinner: {
    width: '32px',
    height: '32px',
    border: '3px solid #E5E7EB',
    borderTop: '3px solid #3B82F6',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '16px',
  },
  
  errorMessage: {
    backgroundColor: '#FEF2F2',
    color: '#DC2626',
    padding: '12px 16px',
    borderRadius: '8px',
    marginTop: '16px',
    border: '1px solid #FECACA',
  },
  
  successMessage: {
    backgroundColor: '#F0FDF4',
    color: '#16A34A',
    padding: '12px 16px',
    borderRadius: '8px',
    marginTop: '16px',
    border: '1px solid #BBF7D0',
  },
  
  // Footer Styles
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '24px',
    borderTop: '1px solid #E5E7EB',
    backgroundColor: '#F8FAFC',
  },
  
  cancelButton: {
    padding: '12px 24px',
    borderRadius: '8px',
    border: '1px solid #D1D5DB',
    backgroundColor: 'transparent',
    color: '#374151',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  
  saveButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    borderRadius: '8px',
    border: 'none',
    backgroundColor: '#3B82F6',
    color: '#FFFFFF',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  
  buttonSpinner: {
    width: '16px',
    height: '16px',
    border: '2px solid rgba(255, 255, 255, 0.3)',
    borderTop: '2px solid #FFFFFF',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
};

// Add CSS keyframes for animations
const styleElement = document.createElement('style');
styleElement.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleElement);

export default ProfileEditModal;